/** @jsxImportSource @emotion/react */
import React from 'react'
import { Select, Tooltip } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { paragraphSmall } from 'src/components/css'

// Registration status options from divisionTableHelper
export const registrationOptions = [
  { value: 1, label: 'Submitted' },
  { value: 2, label: 'Pending' },
  { value: 3, label: 'Approved' },
  { value: 4, label: 'Approved w/ Conditions' },
  { value: 5, label: 'Cancelled' },
]

interface Props {
  selectedStatuses: number[]
  onChange: (statuses: number[]) => void
}

/**
 * Filters registrations by status using a multi-select control.
 *
 * @param selectedStatuses - Currently selected registration status ids.
 * @param onChange - Callback invoked with the updated list of selected statuses.
 */
export const RegistrationStatusFilter: React.FC<Props> = ({
  selectedStatuses,
  onChange,
}) => {
  // Prevent deselecting the last item - user must have at least one option selected
  const handleChange = (statuses: number[]) => {
    if (statuses.length === 0) {
      return
    }
    onChange(statuses)
  }

  const tooltipTitle = selectedStatuses
    .map(
      (status) => registrationOptions.find((opt) => opt.value === status)?.label
    )
    .filter(Boolean)
    .join(', ')

  const suffix = (
    <>
      <span>
        {selectedStatuses.length} / {registrationOptions.length}
      </span>
      <DownOutlined />
    </>
  )

  return (
    <Tooltip title={tooltipTitle} placement='top'>
      <Select
        mode='multiple'
        value={selectedStatuses}
        onChange={handleChange}
        options={registrationOptions}
        maxTagCount={0}
        suffixIcon={suffix}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement || document.body
        }
        css={selectStyle}
        showSearch={false}
        popupMatchSelectWidth={false}
      />
    </Tooltip>
  )
}

const selectStyle = css`
  width: fit-content;
  .ant-select-item-option-content {
    padding-right: 4px !important;
  }
  .ant-select-selector {
    line-height: 1.5 !important;
    padding-right: 80px !important;
    &::before {
      content: 'Status';
      ${paragraphSmall}
      color: ${colors.NEUTRAL_0};
      margin-right: 8px;
      margin-left: 8px;
      align-self: center;
    }
  }
  .ant-select-selection-overflow {
    display: none;
  }
  .ant-select-selection-overflow-item {
    margin-bottom: 4px !important;
    margin-top: 4px !important;
  }
  .ant-select-selection-item-content {
    ${paragraphSmall}
    align-self: center;
    color: ${colors.SECONDARY_300};
  }
  .ant-select-selection-item {
    background-color: ${colors.PRIMARY}!important;
    padding: 4px 8px !important;
    margin: 0 !important;
  }
  .ant-select-selection-item-remove {
    color: ${colors.SECONDARY_300}!important;
  }
  /* Show the suffix icon by removing display: none */
  .ant-select-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: ${colors.NEUTRAL_0};
  }
`
